import { IMOCKUP_ANIMOCK_API_BASE } from '@/config/imockupBuildFlags'
import { fetchWithBaUserToken } from '../../../shared/utils/fetchWithBaUserToken'

/**
 * 导出预检成功返回（解包 BuildAdmin envelope 后的业务字段）。
 */
export type ExportPreflightOk = {
  ok: true
  exportRecordId: number
}

/**
 * AnimockServer `app\common\controller\Api::success()` 默认 `code=1`。
 *
 * 注：`303` 在 `Auth::LOGIN_RESPONSE_CODE` 中表示「需要登录」，对业务接口属于失败语义，
 * 不能视为成功（营销站 `animockApi.ts` 因「登录态校验」专用语义把 303 当成功，仅适用于 auth 流）。
 */
const ANIMOCK_API_SUCCESS_CODES = new Set<number>([1])

/**
 * BuildAdmin / AnimockServer 标准响应结构。
 */
interface AnimockEnvelope<T> {
  code?: number
  msg?: string
  message?: string
  data?: T
}

/**
 * 解析后端可读错误文案：优先 `msg` / `message`，回退 HTTP 状态。
 *
 * @param env - 已解析的 envelope（可能为空）
 * @param httpStatus - HTTP 状态码（无文案时回退）
 */
function resolveAnimockMessage(env: AnimockEnvelope<unknown> | null, httpStatus: number): string {
  const m = env?.msg || env?.message
  if (typeof m === 'string' && m.trim()) return m.trim()
  return `http_${httpStatus}`
}

/**
 * 编辑器导出预检：将元数据登记到 AnimockServer，校验通过后才允许真实导出。
 *
 * @remarks
 * - 直连 AnimockServer（`IMOCKUP_ANIMOCK_API_BASE`），不再绕营销站 Nitro 转发。
 * - 通过 `ba-user-token` 头携带登录态（与营销站 `animockApiFetch` 同名 header）。
 * - URL 追加 `?server=1`：`public/index.php` 据此判定为 API 调用，不会跳转到前端 `index.html`；
 *   相比自定义 `Server: 1` 头更不易因 CORS / 浏览器保留头语义而被忽略，便于排查 token 是否真的发出去。
 *
 * @param payload - 含 `token` 与后端 `preflight` 所需字段
 * @throws Error 网络或业务失败；`message` 优先取后端 `msg`（如「请先登录！」）
 */
export async function postExportPreflight(
  payload: Record<string, unknown> & { token: string },
): Promise<ExportPreflightOk> {
  const base = IMOCKUP_ANIMOCK_API_BASE
  if (!base) {
    throw new Error('animock_api_base_not_configured')
  }

  const { token, ...upstreamBody } = payload
  if (!token || !token.trim()) {
    throw new Error('missing_token')
  }

  const url = `${base}/api/UserExport/preflight?server=1`
  const res = await fetchWithBaUserToken(
    url,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(upstreamBody),
    },
    token,
  )

  const text = await res.text()
  let env: AnimockEnvelope<{ exportRecordId?: number }> | null = null
  if (text) {
    try {
      env = JSON.parse(text) as AnimockEnvelope<{ exportRecordId?: number }>
    } catch {
      env = null
    }
  }

  if (!res.ok) {
    throw new Error(resolveAnimockMessage(env, res.status))
  }
  /** 后端业务失败（如 303 「请先登录！」、400 「Invalid …」）：优先把后端 msg 透传给 UI */
  if (!env || typeof env.code !== 'number' || !ANIMOCK_API_SUCCESS_CODES.has(env.code)) {
    throw new Error(resolveAnimockMessage(env, res.status))
  }
  const id = env.data?.exportRecordId
  if (typeof id !== 'number' || id <= 0) {
    throw new Error('invalid_preflight_response')
  }
  return { ok: true, exportRecordId: id }
}
